import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import { utils } from "../common/utils";
import { authHeader } from "../helpers/auth-header.js";

const CategorySidebar = ({
  showTitle = true,
  className = "sticky top-20",
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const params = {
          keyword: "",
          page: 1,
          perPage: utils.productperPage,
        };

        const { data } = await axios.get(SummaryApi.categoryProduct.url, {
          params,
          headers: authHeader(),
        });

        const allCategories = data?.listCategories?.rows || [];
        const filtered = allCategories.filter((cat) => cat.id !== 10);

        setCategories(
          filtered.map((item) => ({
            id: item.id,
            label: item.name,
            value: item.id.toString(),
            imageUrl: item?.imageUrl || null,
          }))
        );
      } catch (e) {
        console.error("Error fetching categories:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const activeCategoryValues = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const values = [
      ...params.getAll("category"),
      ...params.getAll("categoryId"),
    ].filter(Boolean);
    return new Set(values.map((v) => v.toString()));
  }, [location.search]);

  return (
    <aside className={className}>
      <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
        {showTitle && (
          <h3 className="text-base font-bold text-gray-900 mb-3">
            Categories
          </h3>
        )}

        {loading ? (
          <div className="space-y-2">
            {new Array(8).fill(null).map((_, i) => (
              <div
                key={i}
                className="h-12 rounded-xl bg-gray-100 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="max-h-[calc(100vh-140px)] overflow-y-auto pr-1 scrollbar-none">
            <div className="space-y-2">
              {categories.map((category) => {
                const isActive = activeCategoryValues.has(category.value);

                return (
                  <button
                    key={category.value}
                    type="button"
                    onClick={() => {
                      // categoryProduct page listens to `category` query param.
                      navigate(`/product-category?category=${category.value}`);
                    }}
                    className={`w-full text-left rounded-xl px-4 py-3 transition-all border ${
                      isActive
                        ? "border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50"
                        : "border-gray-100 bg-white hover:bg-gradient-to-r hover:from-orange-50 hover:to-yellow-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 border ${
                          isActive
                            ? "bg-gradient-to-br from-orange-500/15 to-yellow-500/20 border-orange-200"
                            : "bg-gray-50 border-gray-100"
                        }`}
                      >
                        {category.imageUrl ? (
                          <img
                            src={category.imageUrl}
                            alt={category.label}
                            className="object-cover w-full h-full"
                          />
                        ) : (
                          <span
                            className={`text-sm font-bold ${
                              isActive ? "text-orange-700" : "text-gray-700"
                            }`}
                          >
                            {String(category.label || "?").slice(0, 1).toUpperCase()}
                          </span>
                        )}
                      </div>

                      <div className="min-w-0">
                        <div
                          className={`text-sm font-semibold line-clamp-1 transition-colors ${
                            isActive ? "text-orange-700" : "text-gray-900"
                          }`}
                        >
                          {category.label}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default CategorySidebar;

